import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Paper,
  Chip,
  alpha,
} from '@mui/material';
import BaseTab from './BaseTab';

interface FrontendPerformanceTabProps {
  isDark: boolean;
  colors: any;
  discordFont: any;
  glassStyle: any;
  lokiGradient: string;
  goldColor: string;
  isMobile: boolean;
}

const FrontendPerformanceTab: React.FC<FrontendPerformanceTabProps> = (props) => {
  return (
    <BaseTab
      {...props}
      title="Frontend Performance Optimization"
      emoji="🚀"
    >
      <Typography variant="h6" sx={{ color: props.goldColor, mb: 3 }}>
        Performance Optimization Strategies
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: alpha(props.goldColor, 0.05) }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: props.goldColor }}>
                Build Optimizations
              </Typography>
              <List>
                {[
                  'Code Splitting - Route & component based',
                  'Tree Shaking - Remove unused code',
                  'Asset Optimization - Image compression',
                  'Bundle Analysis - Webpack analyzer',
                  'Minification - Production builds',
                ].map((opt, idx) => (
                  <ListItem key={idx}>
                    <ListItemText 
                      primary={opt} 
                      primaryTypographyProps={{ color: props.colors.textPrimary }}
                    />
                    <Chip label="Enabled" size="small" color="success" />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: alpha('#0F3B2E', 0.05) }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: '#0F3B2E' }}>
                Runtime Optimizations
              </Typography>
              <List>
                {[
                  'Lazy Loading - On-demand component loading',
                  'Image Optimization - WebP support',
                  'Caching Strategies - Aggressive caching',
                  'Service Worker - Offline functionality',
                  'Memoization - Prevent re-renders',
                ].map((opt, idx) => (
                  <ListItem key={idx}>
                    <ListItemText 
                      primary={opt} 
                      primaryTypographyProps={{ color: props.colors.textPrimary }}
                    />
                    <Chip label="Enabled" size="small" color="success" />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mb: 2, color: props.goldColor }}>
        Performance Metrics
      </Typography>
      <Grid container spacing={2}>
        {[
          { metric: 'First Contentful Paint', target: '< 1.8s', status: 'good' },
          { metric: 'Largest Contentful Paint', target: '< 2.5s', status: 'good' },
          { metric: 'First Input Delay', target: '< 100ms', status: 'good' },
          { metric: 'Cumulative Layout Shift', target: '< 0.1', status: 'good' },
          { metric: 'Time to Interactive', target: '< 3.8s', status: 'good' },
          { metric: 'Total Blocking Time', target: '< 200ms', status: 'good' },
        ].map((item, idx) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha(props.goldColor, 0.05) }}>
              <Typography variant="caption" color={props.colors.textSecondary}>
                {item.metric}
              </Typography>
              <Typography variant="h6" sx={{ color: item.status === 'good' ? props.goldColor : props.colors.danger }}>
                {item.target}
              </Typography>
              <Chip
                label={item.status === 'good' ? '✓ Achieved' : 'Needs Work'}
                size="small"
                sx={{ 
                  mt: 1,
                  bgcolor: item.status === 'good' ? alpha(props.goldColor, 0.2) : alpha(props.colors.danger, 0.2),
                  color: item.status === 'good' ? props.goldColor : props.colors.danger
                }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>
    </BaseTab>
  );
};

export default FrontendPerformanceTab;